import React, { useState, useEffect } from 'react'
import { useToast, Box, Stack, Alert, Heading, Text, Icon, Button, CircularProgress, Input, Textarea, Select, Checkbox, FormControl, FormLabel, Badge, Table, Tr, Th, Td, Thead, Tbody, Modal, ModalBody, ModalContent, ModalOverlay, ModalHeader, ModalCloseButton, ModalFooter } from '@chakra-ui/react'
import { FaPlus } from 'react-icons/fa'
import DatePicker from 'react-datepicker'
import api from '../../../api'
import utils, { DAYS } from '../../../utils/utils'
import useStore from '../../../store'

import Empty from '../../../images/empty.png'
import EmptyBox from '../../includes/EmptyBox'
import ConfirmButton from '../../includes/ConfirmButton'

const VIA = [
  { name: 'push', label: 'Push notification' },
  { name: 'email', label: 'Email' },
  { name: 'sms', label: 'SMS' },
  { name: 'whatsapp', label: 'WhatsApp' }
]

export default function AutomaticMessages () {
  const [loading, setLoading] = useState(false)
  const [wizardIsOpen, setWizardIsOpen] = useState(false)
  const [stages, setStages] = useState([])
  const [groups, setGroups] = useState([])
  const [automaticMessages, setAutomaticMessages] = useState([])
  const toast = useToast()
  const { trial } = useStore()

  const trialId = trial?._id

  useEffect(() => {
    setLoading(true)
    api.trials.getAutomaticMessages(trialId, data => {
      setLoading(false)
      setAutomaticMessages(data.automaticMessages || [])
    })
    api.trials.getStages(trialId, d => setStages(d.stages))
    api.trials.getGroups(trialId, d => setGroups(d.groups))
  }, [trialId])

  const deleteAutomaticMessage = (messageId) => {
    api.trials.deleteAutomaticMessage(trialId, messageId, () => {
      const newAutomaticMessages = Object.assign([], automaticMessages)
      const index = newAutomaticMessages.map(a => a._id).indexOf(messageId)
      newAutomaticMessages.splice(index, 1)
      setAutomaticMessages(newAutomaticMessages)
      toast({ status: 'success', description: 'Automatic message has been deleted' })
    }, err => {
      toast({ status: 'error', description: err.message })
    })
  }

  return (
    <>
      <Heading mt={5} as='h2' size='lg'>Automatic messages</Heading>
      <Text mb={5}>Set-up automatic messages that are sent to participants on a schedule or as they interact with your trial.</Text>
      <Button colorScheme='primary' onClick={e => setWizardIsOpen(true)}><Icon as={FaPlus} mr={2} /> Create a new automatic message</Button>
      <MessageWizard
        isOpen={wizardIsOpen} trialId={trialId} groups={groups} stages={stages}
        onCreated={newAutomaticMessage => {
          const newAutomaticMessages = Object.assign([], automaticMessages)
          newAutomaticMessages.splice(0, 0, newAutomaticMessage)
          setAutomaticMessages(newAutomaticMessages)
        }}
        onClose={e => setWizardIsOpen(false)}
      />

      {loading && <CircularProgress isIndeterminate />}
      {!loading && !automaticMessages?.length &&
        <EmptyBox
          image={Empty}
          title='Your automatic messages will appear here once created'
          description='Automatic messages enable you to provide automatic support and prompts to your participants as they engage with your trial.'
        />}
      {automaticMessages?.length > 0 &&
        <Table mt={10}>
          <Thead>
            <Tr>
              <Th>Send when</Th>
              <Th>Via</Th>
              <Th>Stage</Th>
              <Th>Message</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {automaticMessages.map(message => {
              return (
                <Tr key={message._id}>
                  <Td>{message.sendWhen}
                    {message.sendWhen === 'schedule' && message.schedule &&
                      <small><br />{utils.getAutomaticMessageScheduleAsString(message, groups)}</small>}
                  </Td>
                  <Td>
                    {message.sendVia?.map(via =>
                      <Badge size='xs' key={via} mr={1}>{VIA.find(v => v.name === via)?.label}</Badge>
                    )}
                    {!message.sendVia?.length && <Badge>Push notification</Badge>}
                  </Td>
                  <Td>{stages.filter(s => s._id === message.stage)[0]?.name || 'N/A'}</Td>
                  <Td><Text fontSize='sm'>{message.message}</Text></Td>
                  <Td style={{ textAlign: 'right' }}>
                    <ConfirmButton
                      header='Really delete this automatic message?'
                      body={'It can\'t be recovered but you can make a new one instead'}
                      trigger={<Button size='xs' colorScheme='red' variant='outline'>Delete</Button>}
                      action={e => deleteAutomaticMessage(message._id)}
                    />
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>}

    </>
  )
}

export function MessageWizard ({ isOpen, onClose, trialId, participant, groups, stages, onCreated }) {
  const [newSendWhen, setNewSendWhen] = useState('schedule')
  const [newSendSchedule, setNewSendSchedule] = useState({})
  const [newMessage, setNewMessage] = useState('')
  const [sendVia, setSendVia] = useState(['push'])
  const toast = useToast()

  useEffect(() => {
    if (participant) {
      setNewSendSchedule({ ...newSendSchedule, participant: participant._id })
    }
  }, [participant])

  const createAutomaticMessage = () => {
    let sendWhen = newSendWhen
    let stage
    if (sendWhen.indexOf('completedStage.') === 0) {
      stage = sendWhen.replace('completedStage.', '')
      sendWhen = 'completedStage'
    }
    api.trials.createAutomaticMessage(trialId, { sendWhen, schedule: newSendSchedule, sendVia, stage, message: newMessage }, automaticMessage => {
      onCreated(automaticMessage)
      onClose()
      toast({ status: 'success', description: 'Automatic message created successfully' })
    }, err => {
      toast({ status: 'error', description: err.message })
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='3xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create a new automatic scheduled message {participant && `for ${participant.id}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {participant &&
            <FormControl>
              <FormLabel>Send to</FormLabel>
              <Text>Participant <Badge>{participant.id}</Badge></Text>
            </FormControl>}

          <FormControl mt={5}>
            <FormLabel htmlFor='message'>Message body</FormLabel>
            <Textarea id='message' placeholder='Write your message...' value={newMessage} onChange={e => setNewMessage(e.target.value)} />
          </FormControl>

          <FormControl mt={5}>
            <FormLabel htmlFor='sendWhen'>What triggers this message</FormLabel>
            <Select id='sendWhen' value={newSendWhen} onChange={e => setNewSendWhen(e.target.value)}>
              <option>Select one</option>
              <option value='schedule'>Send according to a schedule</option>
              <option value='joinedTrial'>Send when the participant joins the trial</option>
              {stages?.map(stage =>
                <option key={stage._id} value={`completedStage.${stage._id}`}>Send when the participant completes stage {stage.name}</option>
              )}
            </Select>
          </FormControl>

          {newSendWhen === 'schedule' &&
            <>
              <FormControl mt={5} p={3} rounded='md' bg='gray.50'>
                <Text fontWeight='bold' mb={5}>Configure your send schedule</Text>
                {!participant &&
                  <Select bg='white' value={newSendSchedule?.group} onChange={e => setNewSendSchedule({ ...newSendSchedule, group: e.target.value || null })}>
                    <option value=''>Send to all participants</option>
                    {groups?.map(g =>
                      <option key={g._id} value={g._id}>Send to participants in {g.name}</option>
                    )}
                  </Select>}

                <Select bg='white' mt={2} value={newSendSchedule?.frequency} onChange={e => setNewSendSchedule({ ...newSendSchedule, frequency: e.target.value })}>
                  <option>Select a schedule...</option>
                  <option value='specific'>Send at specific dates and times</option>
                  <option value='daily'>Send every day</option>
                  <option value='weekly'>Send once per week</option>
                </Select>
                {newSendSchedule?.frequency === 'weekly' &&
                  <Select bg='white' mt={2} value={newSendSchedule?.day} onChange={e => setNewSendSchedule({ ...newSendSchedule, day: parseInt(e.target.value) })}>
                    <option>On...</option>
                    {Array.from({ length: 7 }, (_, i) => i).map(dayIndex =>
                      <option key={dayIndex} value={dayIndex}>On {DAYS[dayIndex]}</option>
                    )}
                  </Select>}
                {['daily', 'weekly'].includes(newSendSchedule?.frequency) &&
                  <Select bg='white' mt={2} value={newSendSchedule?.hour} onChange={e => setNewSendSchedule({ ...newSendSchedule, hour: parseInt(e.target.value) })}>
                    <option>At...</option>
                    {Array.from({ length: 24 }, (_, i) => i).map(hour =>
                      <option key={hour} value={hour}>At {hour < 10 && '0'}{hour}:00</option>
                    )}
                  </Select>}
                {newSendSchedule?.frequency === 'specific' &&
                  <Box mt={5}>
                    <Text fontWeight='semibold' fontSize='sm' mb={2}>Send at the following times:</Text>
                    {!newSendSchedule?.dates?.length &&
                      <Text fontStyle='italic' fontSize='sm' color='orange.600'>No dates and times selected yet</Text>}
                    {newSendSchedule?.dates?.map((date, i) =>
                      <Stack direction='row' key={date} mb={2}>
                        <DatePicker
                          placeholderText='Tap to select a date & time...'
                          showTimeSelect
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode='select'
                          todayButton='Today'
                          dateFormat='dd/MM/yyyy HH:mm'
                          timeIntervals={60}
                          selected={date ? new Date(date) : null}
                          onChange={(d) => {
                            const newDates = Object.assign([], newSendSchedule.dates)
                            newDates[i] = d.toISOString()
                            setNewSendSchedule({ ...newSendSchedule, dates: newDates })
                          }}
                          customInput={<Input bg='white' />}
                        />
                        <Button onClick={e => setNewSendSchedule({ ...newSendSchedule, dates: newSendSchedule.dates.filter((_, j) => j !== i) })}>Remove</Button>
                      </Stack>)}
                    <Button
                      mt={2} colorScheme='blue' variant='outline' size='sm' onClick={e => {
                        const currentDate = new Date()
                        currentDate.setMinutes(0, 0, 0)
                        setNewSendSchedule({ ...newSendSchedule, dates: [...newSendSchedule.dates || [], currentDate] })
                      }}
                    ><Icon as={FaPlus} mr={2} /> Add a date and time
                    </Button>
                  </Box>}
                <Text mt={2} fontSize='sm' color='gray.600'>All times shown are in coordinated universal time (UTC). Your timezone is: {utils.getTimezoneOffset()}.</Text>
              </FormControl>
            </>}

          <FormControl mt={5}>
            <FormLabel htmlFor='sendVia'>Send via</FormLabel>
            <Stack direction='row' id='sendVia'>
              {VIA.map(via =>
                <Checkbox
                  key={via.name} value={via.name} isChecked={sendVia.includes(via.name)} onChange={e => {
                    if (e.target.checked) {
                      setSendVia([...sendVia, via.name])
                    } else {
                      setSendVia(sendVia.filter(v => v !== via.name))
                    }
                  }}
                >{via.label}
                </Checkbox>
              )}
            </Stack>

            {sendVia.includes('push') &&
              <Alert size='sm' mt={3} status='info'>
                Sending via push requires participants to have the mobile app installed and to have agreed or enabled push notifications.
              </Alert>}
            {(sendVia.includes('email') || sendVia.includes('sms') || sendVia.includes('whatsapp')) &&
              <Alert size='sm' mt={3} status='info'>
                Sending via email, SMS or WhatsApp requires participants to have contact details set.
              </Alert>}
            {sendVia.includes('sms') &&
              <Alert size='sm' mt={3} status='warning'>
                Sending a large number of SMS messages may incur significant charges.
              </Alert>}
            {!sendVia.length &&
              <Alert size='sm' mt={3} status='warning'>
                You must select at least one method of sending the message.
              </Alert>}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button ml={5} colorScheme='primary' onClick={createAutomaticMessage}>Complete setup</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
